import React from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from 'react-i18next';
import { usePlannedMealTitle } from '../plannedMealTitle';
import { Paprika, PaprikaGradients, PaprikaLayout } from '../../../../styles/theme';
import { nextMealEyebrowKeys, offersCookNow } from '../nextMealLabel';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { RemoteImage } from '../../../shared/RemoteImage';
import { RecipePlaceholder } from '../../../shared/RecipePlaceholder';

interface Props {
  recipe: RecipeIndexEntry;
  /** Day index of the meal, Monday = 0. */
  targetDi: number;
  /** Day index of today, Monday = 0. */
  todayDi: number;
  /** Meal index — 0 breakfast, 1 lunch, 2 dinner. */
  mi: number;
  /** Committed servings for this meal. */
  servings?: number;
  /** Tapping the card — opens the light detail sheet, same as the row below. */
  onPress: () => void;
  /** The primary action — opens the full recipe, ready to cook. */
  onCook: () => void;
  /** The accompaniments already served with this dish (#746). */
  extras?: HeroExtra[];
  onExtraPress?: (mealId: string) => void;
  onRemoveExtra?: (mealId: string) => void;
  /** An accompaniment to offer under the dish, addable in one tap. */
  side?: RecipeIndexEntry | null;
  onAddSide?: () => void;
  /** Tapping the offered side's name — opens it in the detail sheet. */
  onSidePress?: () => void;
  /** Opens the list of accompaniments. Absent when the slot is full. */
  onMoreSides?: () => void;
}

export interface HeroExtra {
  mealId: string;
  recipe: RecipeIndexEntry;
}

/**
 * "Next meal" hero (#579).
 *
 * The user's complaint was that opening the app doesn't answer "what am I
 * cooking?" — the week list is scrolled somewhere and the dish is below the
 * fold. This card sits above the list and answers it before any scrolling, on
 * any day, whatever state the week is in.
 *
 * Two tiers on purpose: the card opens the detail sheet (cheap, dismissable,
 * identical to tapping the meal in the list), the button commits to the full
 * recipe screen. One control that did both would make the cheap gesture feel
 * expensive.
 *
 * Since #746 the card also carries what is served with the dish. Tonight's
 * dinner lives here and nowhere else (the list drops it), so this is the only
 * place a side can be added to it: one suggestion under the dish, one tap to
 * take it, « Autres » for the rest. Once a side is on the menu it replaces the
 * suggestion.
 *
 * Rendered only when there IS a next meal — WeeklyMobile falls back to the full
 * grocery banner + progress card when the week is done or empty, since the
 * screen space is free at that point anyway.
 */
export function NextMealHero({
  recipe,
  targetDi,
  todayDi,
  mi,
  servings,
  onPress,
  onCook,
  extras = [],
  onExtraPress,
  onRemoveExtra,
  side,
  onAddSide,
  onSidePress,
  onMoreSides,
}: Props) {
  const { t } = useTranslation();
  const title = usePlannedMealTitle(recipe, 'mobile-hero');
  const { dayKey, mealKey } = nextMealEyebrowKeys(targetDi, todayDi, mi);
  const eyebrow = `${t(dayKey)} · ${t(mealKey)}`;
  const showCook = offersCookNow(targetDi, todayDi);

  // Servings as the people icon the list rows use: spelled out, « 6 portions »
  // was cut to « 6 porti… » beside the Cuisiner button on a 344px phone.
  const minutes = recipe.readyInMinutes ? t('thisWeek.minutesShort', { count: recipe.readyInMinutes }) : null;
  const hasMeta = !!minutes || servings != null;

  // The card grows with the content column, so a fixed circle shrinks to a
  // tenth of the card on a tablet. Step it up past the phone range (#632).
  // 56 on a phone since #722: the hero is one row now, Cuisiner included.
  const { width } = useWindowDimensions();
  const circleSize = width >= PaprikaLayout.tabletMinWidth ? 88 : 56;
  const circleStyle = { width: circleSize, height: circleSize, borderRadius: circleSize / 2 };

  return (
    <View style={styles.wrap}>
      <View style={styles.card} testID="next-meal-hero">
        {/* One row since #722: the hero used to stack a card and a full-width
            Cuisiner button, 185px of a 582px phone before the first dinner of
            the list. The two tiers stay — the card opens the light detail sheet,
            the button commits to the full recipe. */}
        <Pressable
          style={({ pressed }) => [styles.row, pressed && styles.cardPressed]}
          onPress={onPress}
          // No button role: on web it would render a <button>, and the Cuisiner
          // button inside it would be a <button> in a <button> — invalid HTML
          // that React reports on every render.
          accessibilityLabel={`${eyebrow} — ${title}`}
        >
          <RemoteImage
            uri={recipe.image}
            variant="thumb"
            style={[styles.circle, circleStyle]}
            resizeMode="cover"
            testID="next-meal-thumb"
            fallback={
              <RecipePlaceholder
                recipe={recipe}
                size={circleSize}
                style={[styles.circle, circleStyle]}
                testID="next-meal-thumb"
              />
            }
          />

          <View style={styles.textBlock}>
            <Text style={styles.eyebrow} numberOfLines={1}>
              {eyebrow.toUpperCase()}
            </Text>
            <Text style={styles.title} numberOfLines={2} testID="planned-meal-title">
              {title}
            </Text>
            {hasMeta ? (
              <View
                style={styles.metaRow}
                accessible
                accessibilityLabel={[minutes, servings != null ? t('thisWeek.servingsShort', { count: servings }) : null]
                  .filter(Boolean)
                  .join(', ')}
              >
                {minutes ? <Text style={styles.meta} numberOfLines={1}>{minutes}</Text> : null}
                {servings != null ? (
                  <View style={styles.metaServings}>
                    <Feather name="users" size={11} color={Paprika.textSecondary} />
                    <Text style={styles.meta}>{servings}</Text>
                  </View>
                ) : null}
              </View>
            ) : null}
          </View>

          {/* Only for a meal later today: on Thursday evening the next meal can
              be Saturday's breakfast, and the card still opens it. */}
          {showCook && (
            <Pressable
              style={({ pressed }) => [styles.ctaWrap, pressed && styles.ctaPressed]}
              onPress={(e) => {
                e.stopPropagation?.();
                onCook();
              }}
              accessibilityRole="button"
              accessibilityLabel={t('thisWeek.cookNow')}
              testID="next-meal-cook"
            >
              <LinearGradient
                colors={PaprikaGradients.cookCta as [string, string]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.cta}
              >
                <Feather name="book-open" size={14} color="#ffffff" />
                <Text style={styles.ctaText}>{t('thisWeek.cookNow')}</Text>
              </LinearGradient>
            </Pressable>
          )}
        </Pressable>

        {extras.length > 0 ? (
          <View style={styles.band} testID="next-meal-extras">
            {extras.map((extra) => (
              <View key={extra.mealId} style={styles.extraRow}>
                <Pressable
                  style={styles.extraBody}
                  onPress={() => onExtraPress?.(extra.mealId)}
                  accessibilityRole="button"
                  accessibilityLabel={extra.recipe.title}
                  testID={`next-meal-extra-${extra.recipe.id}`}
                >
                  <SideThumb recipe={extra.recipe} size={24} />
                  <Text style={styles.extraText} numberOfLines={1}>
                    <Text style={styles.extraWith}>{t('thisWeek.withSide')} </Text>
                    {extra.recipe.title}
                  </Text>
                </Pressable>
                {onRemoveExtra && (
                  <Pressable
                    onPress={() => onRemoveExtra(extra.mealId)}
                    accessibilityRole="button"
                    accessibilityLabel={t('thisWeek.removeSide', { title: extra.recipe.title })}
                    hitSlop={8}
                    testID={`next-meal-extra-remove-${extra.recipe.id}`}
                  >
                    <Feather name="x" size={14} color={Paprika.textMuted} />
                  </Pressable>
                )}
              </View>
            ))}
            {onMoreSides && (
              <Pressable style={styles.addMore} onPress={onMoreSides} accessibilityRole="button" testID="next-meal-more-sides">
                <Feather name="plus" size={12} color={Paprika.primary} />
                <Text style={styles.addMoreText}>{t('thisWeek.addSideLong')}</Text>
              </Pressable>
            )}
          </View>
        ) : side && onAddSide ? (
          <View style={[styles.band, styles.sideRow]} testID="next-meal-side">
            {/* The name opens the same sheet as the dish above it (#854): a
                side nobody can look at is a side nobody takes. */}
            <Pressable
              style={({ pressed }) => [styles.sideBody, pressed && styles.cardPressed]}
              onPress={onSidePress}
              disabled={!onSidePress}
              accessibilityRole="button"
              accessibilityLabel={side.title}
              testID="next-meal-side-open"
            >
              <SideThumb recipe={side} size={32} />
              <View style={styles.sideText}>
                <Text style={styles.sideEyebrow} numberOfLines={1}>{t('thisWeek.goesWith').toUpperCase()}</Text>
                <Text style={styles.sideTitle} numberOfLines={1} testID="next-meal-side-title">
                  {side.title}
                  {side.readyInMinutes ? (
                    <Text style={styles.sideMinutes}> · {t('thisWeek.minutesShort', { count: side.readyInMinutes })}</Text>
                  ) : null}
                </Text>
              </View>
            </Pressable>
            {onMoreSides && (
              <Pressable onPress={onMoreSides} accessibilityRole="button" accessibilityLabel={t('thisWeek.moreSidesLong')} hitSlop={8} testID="next-meal-more-sides">
                <Text style={styles.moreText}>{t('thisWeek.moreSides')}</Text>
              </Pressable>
            )}
            <Pressable
              style={({ pressed }) => [styles.addBtn, pressed && styles.addBtnPressed]}
              onPress={onAddSide}
              accessibilityRole="button"
              accessibilityLabel={`${t('thisWeek.addSideLong')} — ${side.title}`}
              hitSlop={6}
              testID="next-meal-add-side"
            >
              <Feather name="plus" size={15} color={Paprika.primary} />
            </Pressable>
          </View>
        ) : onMoreSides ? (
          <View style={styles.band}>
            <Pressable style={styles.addMore} onPress={onMoreSides} accessibilityRole="button" testID="next-meal-more-sides">
              <Feather name="plus" size={12} color={Paprika.primary} />
              <Text style={styles.addMoreText}>{t('thisWeek.addSideLong')}</Text>
            </Pressable>
          </View>
        ) : null}
      </View>
    </View>
  );
}

function SideThumb({ recipe, size }: { recipe: RecipeIndexEntry; size: number }) {
  const shape = { width: size, height: size, borderRadius: size / 2 };
  return (
    <RemoteImage
      uri={recipe.image}
      variant="thumb"
      style={[styles.circle, shape]}
      resizeMode="cover"
      fallback={<RecipePlaceholder recipe={recipe} size={size} style={[styles.circle, shape]} />}
    />
  );
}

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: 16,
    paddingTop: 10,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  // What is served with the dish: a tonal band under it, not a second card.
  band: {
    backgroundColor: Paprika.surfaceLow,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
    paddingVertical: 8,
    paddingHorizontal: 12,
    gap: 6,
  },
  sideRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  sideBody: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  sideText: {
    flex: 1,
    minWidth: 0,
  },
  sideEyebrow: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 9.5,
    letterSpacing: 0.6,
    color: Paprika.textMuted,
  },
  sideTitle: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 14.5,
    lineHeight: 18,
    color: Paprika.textPrimary,
  },
  sideMinutes: {
    fontFamily: 'Inter-Regular',
    fontSize: 11.5,
    color: Paprika.textSecondary,
  },
  moreText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textSecondary,
    textDecorationLine: 'underline',
  },
  addBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.surfaceLowest,
  },
  addBtnPressed: {
    backgroundColor: Paprika.brandTint,
  },
  extraRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  extraBody: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  extraText: {
    flex: 1,
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textPrimary,
  },
  extraWith: {
    fontFamily: 'Inter-Regular',
    color: Paprika.textSecondary,
  },
  addMore: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    alignSelf: 'flex-start',
    paddingVertical: 2,
  },
  addMoreText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.primary,
  },
  card: {
    borderRadius: 18,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.10)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 2,
  },
  cardPressed: {
    opacity: 0.92,
  },
  circle: {
    // Dimensions come from TABLET_MIN_WIDTH at render; the colour shows while
    // the photo loads. A missing photo draws RecipePlaceholder instead of a
    // blank beige disc, which read as broken (#722).
    backgroundColor: Paprika.bgSunk,
    overflow: 'hidden',
  },
  textBlock: {
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10.5,
    letterSpacing: 0.8,
    color: Paprika.primary,
  },
  // The one editorial moment on this screen — Newsreader carries the dish name,
  // Inter handles the chrome around it (DESIGN.md).
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 17,
    lineHeight: 21,
    color: Paprika.textPrimary,
    marginTop: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 3,
  },
  metaServings: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  meta: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  ctaWrap: {
    borderRadius: 24,
    overflow: 'hidden',
    flexShrink: 0,
  },
  ctaPressed: {
    opacity: 0.9,
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 9,
    paddingHorizontal: 13,
  },
  ctaText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: '#ffffff',
  },
});

export default NextMealHero;
